import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { NotificationService } from 'src/app/notifications/notification.service';
import { SessionService } from 'src/app/session.service';
import { UiService } from 'src/app/ui/ui.service';
import { IntegrationsService } from '../../integrations/integrations.service';
import moment from 'moment-timezone';
import { getAssististDocumentLabels } from '../document-labels';

@Component({
  selector: 'app-thread-detail',
  templateUrl: './thread-detail.component.html',
  styleUrls: ['./thread-detail.component.scss']
})
export class ThreadDetailComponent implements OnInit {
  private onComponentDestroy = new Subject();
  
  integrationId!: string;
  threadId!: string;
  integration: any = null;
  threadData: any = null;
  loading = true;
  error = '';
  JSON = JSON;

  // Cache for expensive operations
  private _cachedMessages: any[] | null = null;
  private _cachedExtractedData: any = null;
  private _cachedCategoriesFromThreadResults: any[] = [];
  private _cachedCategoriesFromExtractedData: any[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private integrationsService: IntegrationsService,
    private sessionService: SessionService,
    private notificationService: NotificationService,
    private uiService: UiService,
  ) {}

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      this.integrationId = params['integrationId'];
      this.threadId = params['threadId'];
      this.loadThreadDetail();
    });
  }

  ngOnDestroy() {
    this.onComponentDestroy.next();
    this.onComponentDestroy.complete();
  }

  loadThreadDetail() {
    this.loading = true;
    this.error = '';

    const masterAccountID = this.sessionService.currentAccountValue?.accountID;

    if (!masterAccountID) {
      this.error = 'No master account ID found';
      this.loading = false;
      return;
    }

    this.integrationsService.getThreadDetail(masterAccountID, +this.integrationId, +this.threadId)
      .subscribe({
        next: (data) => {
          this.threadData = data;
          this.loading = false;
          // Clear cache when new data arrives
          this._cachedMessages = null;
          this._cachedExtractedData = null;
          this._cachedCategoriesFromThreadResults = [];
          this._cachedCategoriesFromExtractedData = [];
        },
        error: (error) => {
          this.error = 'Failed to load thread details';
          this.loading = false;
        }
      });
  }

  formatUserName(user: any): string {
    if (!user) return 'N/A';
    
    const firstName = user.first_name || '';
    const lastName = user.last_name || '';
    
    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    } else if (firstName) {
      return firstName;
    } else if (lastName) {
      return lastName;
    } else {
      return 'N/A';
    }
  }

  formatDate(dateString: string): string {
    if (!dateString) return 'N/A';
    return moment(dateString).format('MMM DD YYYY, h:mm A');
  }

  formatMessageTime(unixTimestamp: number): string {
    if (!unixTimestamp) return 'N/A';
    return moment.unix(unixTimestamp).format('MMM DD YYYY, h:mm A');
  }

  formatRating(rating?: number): string {
    if (!rating) return 'No rating provided';
    return `${rating}/5`;
  }

  formatDeviceInfo(device?: any): string {
    if (!device) return 'No device information';
    
    const system = device.system_name || 'Unknown OS';
    const version = device.system_version || '';
    const make = device.make || 'Unknown';
    const model = device.model || '';
    
    let deviceString = '';
    
    if (make === 'web') {
      deviceString = `${system} ${version} - ${model}`;
    } else {
      deviceString = `${system} ${version} - ${make} ${model}`;
    }
    
    return deviceString.trim();
  }

  get reversedMessages(): any[] {
    if (!this.threadData?.openai_history) return [];
    
    // Return cached result if available
    if (this._cachedMessages) {
      return this._cachedMessages;
    }
    
    this._cachedMessages = this.threadData.openai_history;
    return this._cachedMessages || [];
  }

  formatMessageContent(content: any): string {
    if (!content || !Array.isArray(content)) return '';
    
    let formattedContent = '';
    
    for (const item of content) {
      if (item.type === 'text' && item.text?.value) {
        const text = item.text.value;
        
        // Check if this message contains JSON with categories
        if (text.includes('```json') && text.includes('"name"')) {
          try {
            // Extract the JSON part
            const jsonStart = text.indexOf('```json') + 7;
            const jsonEnd = text.lastIndexOf('```');
            const jsonText = text.substring(jsonStart, jsonEnd).trim();
            const jsonData = JSON.parse(jsonText);
            
            // Extract category names
            const categoryNames = jsonData
              .filter((category: any) => category.name)
              .map((category: any) => category.name);
            
            // Get the text before the JSON
            const textBeforeJson = text.substring(0, text.indexOf('```json')).trim();
            
            // Format the message
            formattedContent = `${textBeforeJson} ${categoryNames.join(', ')}`;
          } catch (error) {
            // If JSON parsing fails, return original text
            formattedContent = text;
          }
        } else {
          formattedContent = text;
        }
      }
    }
    
    return formattedContent;
  }

  getDisplayMessageContent(message: any): string {
    const formattedText = this.formatMessageContent(message?.content);
    if (formattedText && formattedText.trim().length > 0) {
      return formattedText;
    }

    const choiceCall = this.getPresentChoiceToolCalls(message)[0];
    if (choiceCall?.prompt) {
      return choiceCall.prompt;
    }

    return '';
  }

  hasToolCalls(message: any): boolean {
    return this.getToolCallsForMessage(message).length > 0;
  }

  getToolCallsForMessage(message: any): any[] {
    const directToolCalls = Array.isArray(message?.tool_calls) ? message.tool_calls : [];
    if (directToolCalls.length > 0) {
      return directToolCalls;
    }

    const content = Array.isArray(message?.content) ? message.content : [];
    return content
      .filter((item: any) => item?.type === 'tool_call' && item?.tool_call)
      .map((item: any) => item.tool_call);
  }

  parseToolCallArguments(toolCall: any): any {
    try {
      const rawArguments = toolCall?.function?.arguments;
      return typeof rawArguments === 'string' ? JSON.parse(rawArguments) : null;
    } catch {
      return null;
    }
  }

  getFormattedToolCallData(message: any): any {
    const toolCalls = this.getToolCallsForMessage(message);
    const finalResultsCall = toolCalls.find((toolCall: any) => toolCall?.function?.name === 'final_results');
    if (finalResultsCall) {
      const args = this.parseToolCallArguments(finalResultsCall);
      if (args) {
        return args;
      }
    }

    if (this.threadData?.thread_results?.results) {
      return this.threadData.thread_results.results;
    }

    return null;
  }

  getCategoriesFromToolCall(message: any): any[] {
    const data = this.getFormattedToolCallData(message);
    if (data?.categories) {
      return data.categories;
    }
    if (data?.query_results?.arguments?.categories) {
      return data.query_results.arguments.categories;
    }
    return [];
  }

  getPresentChoiceToolCalls(message: any): any[] {
    return this.getToolCallsForMessage(message)
      .filter((toolCall: any) => toolCall?.function?.name === 'present_choice_options')
      .map((toolCall: any) => {
        const args = this.parseToolCallArguments(toolCall) || {};
        return {
          choice_kind: args.choice_kind || null,
          prompt: args.prompt || '',
          options: Array.isArray(args.options) ? args.options : []
        };
      });
  }

  get categoriesFromThreadResults(): any[] {
    if (!this.threadData?.thread_results?.results) {
      return [];
    }

    // Return cached result if available
    if (this._cachedCategoriesFromThreadResults.length > 0) {
      return this._cachedCategoriesFromThreadResults;
    }

    const results = this.threadData.thread_results.results;
    
    // Check for categories in tool_calls function arguments
    if (results.tool_calls && results.tool_calls.length > 0) {
      for (const toolCall of results.tool_calls) {
        if (toolCall.function?.name === 'final_results' && toolCall.function?.arguments) {
          try {
            const args = JSON.parse(toolCall.function.arguments);
            if (args.categories) {
              this._cachedCategoriesFromThreadResults = args.categories;
              return this._cachedCategoriesFromThreadResults;
            }
          } catch {
          }
        }
      }
    }
    
    // Check for categories in query_results
    if (results.query_results?.arguments?.categories) {
      this._cachedCategoriesFromThreadResults = results.query_results.arguments.categories;
      return this._cachedCategoriesFromThreadResults;
    }
    
    // Check for categories directly in results
    if (results.categories) {
      this._cachedCategoriesFromThreadResults = results.categories;
      return this._cachedCategoriesFromThreadResults;
    }
    
    return [];
  }

  getMessageContent(message: any): string {
    if (!message.content || !Array.isArray(message.content)) {
      return 'No content';
    }

    const textContent = message.content.find((item: any) => item.type === 'text');
    return textContent?.text?.value || 'No text content';
  }

  getMessageRole(message: any, index?: number): string {
    // If this is the first chronological message (first in array), show integration type
    if (index !== undefined && index === 0) {
      return this.getIntegrationType().toUpperCase();
    }
    return message.role?.toUpperCase() || 'UNKNOWN';
  }

  getIntegrationType(): string {
    const featureType = this.route.snapshot.params['featureType'];
    return featureType === 'tripist' ? 'TRIPIST' : featureType.toUpperCase();
  }

  getSectionTitle(): string {
    const featureType = this.route.snapshot.params['featureType'];
    return `${getAssististDocumentLabels(featureType).singular} Data`;
  }

  get documentLabelSingular(): string {
    return getAssististDocumentLabels(this.route.snapshot.params['featureType']).singular;
  }

  get documentLabelPlural(): string {
    return getAssististDocumentLabels(this.route.snapshot.params['featureType']).plural;
  }

  get extractedData(): any {
    if (!this.threadData?.openai_history) return null;
    
    // Return cached result if available
    if (this._cachedExtractedData) {
      return this._cachedExtractedData;
    }
    
    const messages = this.reversedMessages;
    if (messages.length === 0) return null;
    
    const firstMessage = messages[0]; // First chronological message
    if (!firstMessage?.content) return null;
    
    // Look for JSON data in the text content
    for (const item of firstMessage.content) {
      if (item.type === 'text' && item.text?.value) {
        const text = item.text.value;
        
        // Check if this message contains JSON with categories
        if (text.includes('```json') && text.includes('"name"')) {
          try {
            // Extract the JSON part
            const jsonStart = text.indexOf('```json') + 7;
            const jsonEnd = text.lastIndexOf('```');
            const jsonText = text.substring(jsonStart, jsonEnd).trim();
            
            const jsonData = JSON.parse(jsonText);
            
            // Transform the data structure to match what we expect
            const transformedCategories = jsonData.map((category: any) => ({
              id: category.id,
              name: category.name,
              totalDocuments: category.totalDocuments,
              tag_groups: category.availableTagGroups?.map((tagGroup: any) => {
                const groupName = Object.keys(tagGroup)[0]; // e.g., "features"
                const tagCounts = Object.values(tagGroup)[0] as Record<string, number> || {}; // e.g., {"american cuisine": 1, "artisan": 1, ...}
                
                return {
                  group_name: groupName,
                  tags: Object.keys(tagCounts), // Extract just the tag names
                  tag_behavior: "OR"
                };
              }) || []
            }));
            
            this._cachedExtractedData = { categories: transformedCategories };
            return this._cachedExtractedData;
          } catch (error) {
            // Silently handle parsing errors
            return null;
          }
        }
      }
    }
    
    return null;
  }

  get categoriesFromExtractedData(): any[] {
    // Return cached result if available
    if (this._cachedCategoriesFromExtractedData.length > 0) {
      return this._cachedCategoriesFromExtractedData;
    }
    
    const data = this.extractedData;
    if (data?.categories) {
      this._cachedCategoriesFromExtractedData = data.categories;
      return this._cachedCategoriesFromExtractedData;
    }
    if (data?.query_results?.arguments?.categories) {
      this._cachedCategoriesFromExtractedData = data.query_results.arguments.categories;
      return this._cachedCategoriesFromExtractedData;
    }
    
    this._cachedCategoriesFromExtractedData = [];
    return this._cachedCategoriesFromExtractedData;
  }

  getMessageTime(message: any): string {
    return moment.unix(message.created_at).format("MMM DD YYYY, h:mm A");
  }

  goBack() {
    this.router.navigate(['../'], { relativeTo: this.route });
  }

  hasThreadResults(): boolean {
    return this.threadData?.thread_results && 
           this.threadData.thread_results.results?.tool_calls?.length > 0;
  }

  get auraBookingEvents(): any[] {
    return this.threadData?.aura_booking_events || [];
  }

  formatBookingEventLabel(event: any): string {
    const type = String(event?.event_type || '').replace(/_/g, ' ');
    return type ? `${type[0].toUpperCase()}${type.slice(1)}` : 'Booking Event';
  }

  getToolCallResults(): any[] {
    return this.threadData?.thread_results?.results?.tool_calls || [];
  }

  formatToolCallResult(toolCall: any): string {
    try {
      const args = JSON.parse(toolCall.function.arguments);
      return JSON.stringify(args, null, 2);
    } catch {
      return toolCall.function.arguments || 'Invalid JSON';
    }
  }

  get queryResults(): any[] {
    if (!this.threadData?.thread_results?.results?.query_results?.results) {
      return [];
    }
    return this.threadData.thread_results.results.query_results.results;
  }
}
